import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '@fontsource/ibm-plex-sans/700.css';
import '@fontsource/ibm-plex-sans-arabic/400.css';
import '@fontsource/ibm-plex-sans-arabic/500.css';
import '@fontsource/ibm-plex-sans-arabic/600.css';
import '@fontsource/ibm-plex-sans-arabic/700.css';
import 'primeicons/primeicons.css';
import 'material-symbols/outlined.css';
import './tokens.css';
import './data-table.css';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import type { Plugin } from 'vue';
import ConfirmationService from 'primevue/confirmationservice';
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';

/**
 * Jervis brand preset on top of Aura, translated from the Stitch project's
 * DESIGN.md token export (see `docs/design-system.md` — Material Design 3
 * role naming, seed color #3730A3). See `tokens.css` for the raw hex ramps;
 * this file wires them into PrimeVue's primitive/semantic token layers so
 * every component (Button, Tag, Message, DataTable, ...) picks them up
 * without per-screen overrides.
 *
 * - `indigo` (primitive) is overridden with the captured primary ramp and
 *   promoted to `semantic.primary` — brand buttons, active nav, focus rings.
 * - `green`/`amber`/`orange` (Aura's `success`/`warn` severity primitives)
 *   are overridden with the captured secondary (teal) and tertiary
 *   (terracotta) ramps, per the token notes: teal = positive/status-ok,
 *   terracotta = warning/attention. This lets existing `severity="success"`
 *   Tags/Messages/Buttons across the app re-skin for free.
 * - `red` (the `danger` severity primitive) is overridden with the captured
 *   error ramp (`#ba1a1a` family), which is closer to the real design than
 *   a generic red and is used the same way (destructive actions, 403/error
 *   states, validation).
 */
const JervisPreset = definePreset(Aura, {
  primitive: {
    indigo: {
      50: '#f1f0ff',
      100: '#e2dfff',
      200: '#c3c0ff',
      300: '#a9a7ff',
      400: '#8b85f0',
      500: '#6355d6',
      600: '#3730a3',
      700: '#1f108e',
      800: '#170a6e',
      900: '#100650',
      950: '#0f0069',
    },
    green: {
      50: '#f0fdfa',
      100: '#ccfbf1',
      200: '#9cf2e8',
      300: '#80d5cb',
      400: '#4fd1c5',
      500: '#14b8a6',
      600: '#006f67',
      700: '#006a63',
      800: '#00524c',
      900: '#003c37',
      950: '#00201c',
    },
    amber: {
      50: '#fff3ea',
      100: '#ffe0cc',
      200: '#ffbf94',
      300: '#fe9562',
      400: '#f2601e',
      500: '#c2410c',
      600: '#752c00',
      700: '#5c2300',
      800: '#511c00',
      900: '#3d1500',
      950: '#2a0f00',
    },
    orange: {
      50: '#fff3ea',
      100: '#ffe0cc',
      200: '#ffbf94',
      300: '#fe9562',
      400: '#f2601e',
      500: '#c2410c',
      600: '#752c00',
      700: '#5c2300',
      800: '#511c00',
      900: '#3d1500',
      950: '#2a0f00',
    },
    red: {
      50: '#fff5f4',
      100: '#ffdad6',
      200: '#ffb4ab',
      300: '#ff8a80',
      400: '#de3730',
      500: '#ba1a1a',
      600: '#93000a',
      700: '#740006',
      800: '#530003',
      900: '#410002',
      950: '#2d0001',
    },
  },
  semantic: {
    colorScheme: {
      light: {
        primary: {
          color: '{indigo.700}',
          contrastColor: '#ffffff',
          hoverColor: '{indigo.600}',
          activeColor: '{indigo.800}',
        },
      },
    },
    primary: {
      50: '{indigo.50}',
      100: '{indigo.100}',
      200: '{indigo.200}',
      300: '{indigo.300}',
      400: '{indigo.400}',
      500: '{indigo.500}',
      600: '{indigo.600}',
      700: '{indigo.700}',
      800: '{indigo.800}',
      900: '{indigo.900}',
      950: '{indigo.950}',
    },
  },
});

/**
 * Installs PrimeVue with the Jervis theme. Text direction is not configured here:
 * components follow the document `dir` attribute, which the app sets from the active locale.
 */
export const JervisUi: Plugin = {
  install(app) {
    app.use(PrimeVue, {
      ripple: false,
      inputVariant: 'outlined',
      theme: {
        preset: JervisPreset,
        options: {
          // ADR-109: use the captured light reference by default, including on dark-OS hosts.
          // Explicit dark opt-in must agree with tokens.css and Tailwind's dark variant.
          darkModeSelector: '[data-theme="dark"]',
          // Tailwind utilities must be able to override component styles (see app styles.css layer order).
          cssLayer: { name: 'primevue', order: 'theme, base, primevue' },
        },
      },
    });
    app.use(ToastService);
    app.use(ConfirmationService);
  },
};
